import { computed, Injectable, signal } from '@angular/core';
import { AgentWorkspaceContext, AgentWorkspaceSource } from '../interfaces/agent-session';

interface WorkspaceRecord {
  source: AgentWorkspaceSource;
  context: AgentWorkspaceContext;
  signature: string;
}

@Injectable({ providedIn: 'root' })
export class AgentWorkspaceRegistry {
  private readonly contexts = signal<ReadonlyMap<string, AgentWorkspaceContext>>(new Map());
  private readonly records = new Map<string, WorkspaceRecord>();
  private readonly selectedTargets = new Map<string, { modelUuid: string; hierarchy: string }>();
  private owner: object | null = null;
  private topology = '';
  private refresh: (() => void) | undefined;
  readonly appRevision = signal(0);
  readonly activeId = signal('application');
  readonly active = computed(() => this.contexts().get(this.activeId()) ?? this.applicationContext());

  sync(owner: object, sources: readonly AgentWorkspaceSource[], selectedId?: string, refresh?: () => void): void {
    this.refresh = refresh;
    this.owner = owner;
    const topology = JSON.stringify([sources.map(source => [source.id, source.context.tabType]), selectedId]);
    if (topology !== this.topology) {
      this.topology = topology;
      this.appRevision.update(value => value + 1);
    }
    let changed = false;
    const existing = new Set(sources.map(source => source.id));
    for (const id of this.records.keys()) {
      if (!existing.has(id)) { this.records.delete(id); changed = true; }
    }
    for (const source of sources) {
      const represented = source.context.representedModels ?? [];
      const selected = this.selectedTargets.get(source.id);
      const selectedStillRepresented = selected && represented.some(model => model.modelUuid === selected.modelUuid && model.hierarchy === selected.hierarchy);
      if (selected && !selectedStillRepresented) this.selectedTargets.delete(source.id);
      const sourceHasTarget = source.context.modelTargetState === 'resolved' && !!source.context.modelUuid && !!source.context.hierarchy;
      const contextSource = !sourceHasTarget && selectedStillRepresented
        ? { ...source.context, modelTargetState: 'resolved' as const, modelUuid: selected!.modelUuid, hierarchy: selected!.hierarchy }
        : source.context;
      // Do not turn an automatically resolved single-Model Canvas into a
      // durable choice. Once another View is imported that old implicit
      // target must disappear and the agent must ask which Model to use.
      // selectedTargets is written only by selectRepresentedModel(), i.e. a
      // user-confirmed Structured Choice.
      const previous = this.records.get(source.id);
      const signature = JSON.stringify(contextSource);
      const identityChanged = !!previous && (
        previous.source.instance !== source.instance ||
        previous.source.lifecycleRevision !== source.lifecycleRevision ||
        previous.context.tabType !== contextSource.tabType ||
        previous.context.dataServiceId !== contextSource.dataServiceId ||
        previous.context.modelUuid !== contextSource.modelUuid ||
        previous.context.modelTargetState !== contextSource.modelTargetState ||
        previous.context.hierarchy !== contextSource.hierarchy ||
        JSON.stringify(previous.context.representedModels) !== JSON.stringify(contextSource.representedModels) ||
        previous.context.viewUuid !== contextSource.viewUuid
      );
      if (!previous || identityChanged || previous.signature !== signature) {
        const context = Object.freeze({ ...contextSource, kind: 'tab' as const,
          scopeId: source.id, tabInstanceId: source.id,
          contextRevision: (previous?.context.contextRevision ?? 0) + (identityChanged ? 1 : 0) });
        this.records.set(source.id, { source, context, signature });
        changed = true;
      }
    }
    if (changed) this.contexts.set(new Map([...this.records].map(([id, record]) => [id, record.context])));
    this.activeId.set(selectedId && existing.has(selectedId) ? selectedId : 'application');
  }

  selectRepresentedModel(scopeId: string, modelUuid: string): boolean {
    this.refresh?.();
    const current = this.contexts().get(scopeId);
    const matches = current?.representedModels?.filter(model => model.modelUuid === modelUuid) ?? [];
    if (matches.length !== 1) return false;
    this.selectedTargets.set(scopeId, { modelUuid: matches[0].modelUuid, hierarchy: matches[0].hierarchy });
    this.refresh?.();
    return true;
  }

  /** A model choice belongs to a Structured Choice continuation, not to later chat turns. */
  clearRepresentedModel(scopeId: string): void {
    if (!this.selectedTargets.delete(scopeId)) return;
    this.refresh?.();
  }

  detach(owner: object): void {
    if (owner !== this.owner) return;
    this.owner = null;
    this.refresh = undefined;
    this.records.clear();
    this.selectedTargets.clear();
    this.contexts.set(new Map());
    this.activeId.set('application');
    this.topology = '';
    this.appRevision.update(value => value + 1);
  }

  capture(scopeId = this.activeId()): AgentWorkspaceContext | null {
    this.refresh?.();
    const context = scopeId === 'application' ? this.applicationContext() : this.contexts().get(scopeId);
    return context ? Object.freeze({ ...context }) : null;
  }

  applicationMetadata(): { app_revision: number; tabs: { id: string; label: string; type: string | undefined }[]; active_tab_id: string | null } {
    this.refresh?.();
    return { app_revision: this.appRevision(),
      tabs: [...this.contexts().values()].map(context => ({ id: context.scopeId, label: context.label, type: context.tabType })),
      active_tab_id: this.activeId() === 'application' ? null : this.activeId() };
  }

  instance(snapshot: AgentWorkspaceContext): object | null {
    if (!this.isCurrent(snapshot)) return null;
    return this.records.get(snapshot.scopeId)?.source.instance ?? null;
  }

  isCurrent(snapshot: AgentWorkspaceContext): boolean {
    this.refresh?.();
    if (snapshot.kind === 'application') return snapshot.contextRevision === this.appRevision();
    const current = this.contexts().get(snapshot.scopeId);
    return !!current && !current.loading && current.contextRevision === snapshot.contextRevision;
  }

  /**
   * A saved-View import is a high-level request to merge into the current
   * Creator. Its own asynchronous loading advances contextRevision, so it is
   * intentionally less strict than isCurrent(). It never authorizes a Canvas
   * patch or a canonical Model mutation; it only proves that the same ready
   * Creator and Data Service are still present for a RESTORE_VIEW action.
   */
  isCompatibleRestoreTarget(snapshot: AgentWorkspaceContext): boolean {
    this.refresh?.();
    if (snapshot.kind !== 'tab') return false;
    const current = this.contexts().get(snapshot.scopeId);
    return !!current && !current.loading
      && current.kind === 'tab'
      && current.tabInstanceId === snapshot.tabInstanceId
      && current.tabType === snapshot.tabType
      && current.dataServiceId === snapshot.dataServiceId;
  }

  private applicationContext(): AgentWorkspaceContext {
    return Object.freeze({ kind: 'application', scopeId: 'application', label: 'Application',
      viewUuid: null, representedModels: [], contextRevision: this.appRevision(), loading: false });
  }
}
